/**
 * AsyncSearchCombobox — generic, single-select, debounced async picker.
 *
 * A small self-contained combobox for the org PersonDrawer inline-mutation
 * panels (manager / worker / target-position pickers). Built on the shared
 * `@/components/ui/popover` primitive plus an input and a keyboard-navigable
 * results list. No app-specific coupling — the caller supplies `search` and
 * the accessor functions, so it works over EmployeeData, Position, or anything.
 *
 * Behaviour:
 *   - debounced 300ms, minimum 2 chars before it queries
 *   - Arrow up/down move the highlight, Enter selects, Escape closes
 *   - request-race guard: only the latest query's results are rendered
 *
 * (Pattern mirrors the hr-ops LookupField, but nothing is imported from that
 * app — hr-ops is a separate build. Styling follows this app's shadcn tokens.)
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Search, Loader2, Check, ChevronsUpDown } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export interface AsyncSearchComboboxProps<T> {
  /** Query the backend for matches. Called with the debounced term. */
  search: (query: string) => Promise<T[]>;
  /** Stable unique key for an item (React key + selection identity). */
  getKey: (item: T) => string;
  /** Primary display line for an item. */
  getPrimary: (item: T) => string;
  /** Optional secondary/muted display line for an item. */
  getSecondary?: (item: T) => string | undefined;
  /** Fired when the user picks an item. */
  onSelect: (item: T) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Minimum chars before searching. Default 2. */
  minChars?: number;
  /** Debounce window in ms. Default 300. */
  debounceMs?: number;
  /** Empty-results copy. */
  emptyText?: string;
  className?: string;
}

export function AsyncSearchCombobox<T>({
  search,
  getKey,
  getPrimary,
  getSecondary,
  onSelect,
  placeholder = 'Search…',
  disabled = false,
  minChars = 2,
  debounceMs = 300,
  emptyText = 'No matches found.',
  className,
}: AsyncSearchComboboxProps<T>) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  // Monotonic request token so a slow earlier query can't overwrite a newer one.
  const reqSeq = useRef(0);

  // Debounced search
  useEffect(() => {
    const term = query.trim();
    if (term.length < minChars) {
      setResults([]);
      setLoading(false);
      setActive(-1);
      return;
    }
    // Drop the previous query's results/highlight the moment the term changes,
    // so an Enter during the debounce/in-flight window can't commit a stale row.
    setResults([]);
    setActive(-1);
    setLoading(true);
    const seq = ++reqSeq.current;
    const timer = setTimeout(() => {
      search(term)
        .then((items) => {
          if (seq !== reqSeq.current) return; // stale
          setResults(items);
          setActive(items.length > 0 ? 0 : -1);
        })
        .catch(() => {
          if (seq !== reqSeq.current) return;
          setResults([]);
          setActive(-1);
        })
        .finally(() => {
          if (seq === reqSeq.current) setLoading(false);
        });
    }, debounceMs);
    return () => clearTimeout(timer);
  }, [query, minChars, debounceMs, search]);

  // Focus the input when the popover opens.
  useEffect(() => {
    if (open) {
      const id = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
  }, [open]);

  const choose = useCallback(
    (item: T) => {
      setSelectedLabel(getPrimary(item));
      onSelect(item);
      setOpen(false);
      setQuery('');
      setResults([]);
      setActive(-1);
    },
    [getPrimary, onSelect],
  );

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (results.length ? Math.min(i + 1, results.length - 1) : -1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (results.length ? Math.max(i - 1, 0) : -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // Only act on settled results for the CURRENT query — never a stale row
      // still on screen while a newer query is debouncing/in flight.
      if (!loading && active >= 0 && active < results.length) choose(results[active]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  // Keep the highlighted row scrolled into view.
  useEffect(() => {
    if (active < 0 || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(`[data-idx="${active}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const triggerLabel = useMemo(
    () => selectedLabel ?? placeholder,
    [selectedLabel, placeholder],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'flex h-9 w-full items-center gap-2 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs outline-none transition-[color,box-shadow]',
            'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
            'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
            className,
          )}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span
            className={cn(
              'min-w-0 flex-1 truncate text-left',
              selectedLabel ? 'text-foreground' : 'text-muted-foreground',
            )}
          >
            {triggerLabel}
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] min-w-[16rem] p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="border-b border-border p-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={placeholder}
              className="h-8 pl-8"
              role="combobox"
              aria-controls="async-combobox-list"
              aria-expanded={open}
            />
          </div>
        </div>
        <div
          ref={listRef}
          id="async-combobox-list"
          role="listbox"
          className="max-h-64 overflow-y-auto p-1"
        >
          {loading ? (
            <div className="flex items-center gap-2 px-2 py-3 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Searching…
            </div>
          ) : query.trim().length < minChars ? (
            <div className="px-2 py-3 text-sm text-muted-foreground">
              Type at least {minChars} characters to search.
            </div>
          ) : results.length === 0 ? (
            <div className="px-2 py-3 text-sm text-muted-foreground">{emptyText}</div>
          ) : (
            results.map((item, idx) => {
              const secondary = getSecondary?.(item);
              return (
                <button
                  key={getKey(item)}
                  type="button"
                  data-idx={idx}
                  role="option"
                  aria-selected={idx === active}
                  onMouseEnter={() => setActive(idx)}
                  onClick={() => choose(item)}
                  className={cn(
                    'flex w-full items-start gap-2 rounded-sm px-2 py-2 text-left text-sm transition-colors',
                    idx === active ? 'bg-muted' : 'hover:bg-muted/60',
                  )}
                >
                  <Check
                    className={cn(
                      'mt-0.5 h-3.5 w-3.5 shrink-0',
                      idx === active ? 'text-heritage-blue' : 'text-transparent',
                    )}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium text-foreground">
                      {getPrimary(item)}
                    </span>
                    {secondary && (
                      <span className="block truncate text-xs text-muted-foreground">
                        {secondary}
                      </span>
                    )}
                  </span>
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default AsyncSearchCombobox;
